import { formatDate } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { PortalService } from '@vsolv/packages/portal-config/web';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { MenuTriggerForDirective } from '@vsolv/vectors-ui/menu';
import { LinksWebService } from '@wsphere/links/web';
import { Claim, Coverage, Warranty, WarrantyDocument } from '@wsphere/warranties/domain';
import { ClaimService } from '@wsphere/warranties/web';
import moment from 'moment';
import { firstValueFrom, map } from 'rxjs';
import { WarrantyCustomerPortalService } from '../../../services/warranties.service';

/** Uploaded new files, by outcome. */
interface UploadOutcome {
  success: number;
  error: number;
}

/** Selected repository documents, by outcome. A whole-request failure counts every selection as failed. */
interface LinkOutcome {
  success: number;
  unavailable: number;
  error: number;
}

const EVIDENCE_STEP = 3;
const LinkStatus = Claim.Documents.LinkRepositoryDocumentStatus;

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-new-claim-page',
  templateUrl: './new-claim.page.html',
  standalone: false,
})
export class NewClaimPage {
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private configSvc: PortalService,
    private claimSvc: ClaimService,
    private formBuilder: FormBuilder,
    private toastSvc: ToastService,
    private linkSvc: LinksWebService,
    private warrantySvc: WarrantyCustomerPortalService
  ) {}

  protected readonly today = formatDate(new Date(), 'yyyy-MM-dd', 'en');
  protected readonly maxDate = new Date();

  protected claimFormGroup = this.formBuilder.group({
    complaint: [null as string | null, Validators.required],
    // Local 'yyyy-MM-dd' string, so the picked day never shifts by timezone.
    lossDate: [null as string | null],
    coverages: [[] as (Coverage.Model | undefined)[]],
    description: [null as string | null, Validators.required],
    evidence: [[] as File[]],
    // Only what the customer ticks; nothing from the repository is ever preselected.
    repositoryDocumentIds: [[] as string[]],
  });

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  protected readonly warranty$ = this.route.parent!.data.pipe(map(data => data['warranty'] as Warranty.Model));
  protected readonly coverageId$ = this.route.queryParams.pipe(map(data => data['coverage'] as string));

  protected readonly config = this.configSvc.config;

  protected readonly maxRepositoryDocuments = Claim.Documents.MAX_LINK_REPOSITORY_DOCUMENTS;
  protected readonly categoryLabels = WarrantyDocument.CATEGORY_LABELS;
  private readonly repositoryPageSize = 10;
  protected repositoryDocuments: WarrantyDocument.DocumentResponse[] = [];
  protected repositoryLoading = false;
  protected repositoryError = false;
  private repositoryPage = 0;
  private repositoryTotalPages = 0;

  protected step = 0;

  protected lastStep = 5;

  protected get newFileCount() {
    return this.claimFormGroup.value.evidence?.length ?? 0;
  }

  protected get repositoryDocumentCount() {
    return this.selectedRepositoryDocumentIds.length;
  }

  protected get repositoryLimitReached() {
    return this.repositoryDocumentCount >= this.maxRepositoryDocuments;
  }

  protected get repositoryHasMore() {
    return this.repositoryPage < this.repositoryTotalPages;
  }

  private get selectedRepositoryDocumentIds() {
    return this.claimFormGroup.controls.repositoryDocumentIds.value ?? [];
  }

  protected nextPage(warrantyId?: string) {
    if (this.step >= this.lastStep) return;

    this.step++;

    // Loaded on first visit to the evidence step, so claims without evidence never request it.
    if (this.step === EVIDENCE_STEP && !this.repositoryPage && !this.repositoryLoading) {
      void this.loadRepositoryDocuments();
    }

    if (this.step === this.lastStep && warrantyId) this.openClaim(warrantyId);

    if (this.step > this.lastStep) this.step = this.lastStep;
  }

  protected async previousPage() {
    if (this.step <= 0) {
      this.clear();
      return;
    }

    this.step--;

    if (this.step < 0) this.step = 0;
  }

  protected clear() {
    this.step = 0;
  }

  toPickerDate(value: string | null | undefined) {
    return value ? moment(value).toDate() : null;
  }

  pickLossDate(date: Date | null, trigger: MenuTriggerForDirective) {
    this.claimFormGroup.controls.lossDate.setValue(date ? formatDate(date, 'yyyy-MM-dd', 'en') : null);
    trigger.close();
  }

  checkForOther() {
    const coverages = this.claimFormGroup.value.coverages;
    if (coverages && coverages.length) return coverages.includes(undefined);
    return false;
  }

  /** Loads the next page of the warranty's repository; also retries a page that failed. */
  async loadRepositoryDocuments() {
    if (this.repositoryLoading) return;
    this.repositoryLoading = true;
    this.repositoryError = false;

    try {
      const warranty = await firstValueFrom(this.warranty$);
      const page = this.repositoryPage + 1;
      const response = await this.warrantySvc.listWarrantyDocuments(warranty.id, {
        page,
        limit: this.repositoryPageSize,
      });

      // A document can shift pages if another tab uploads meanwhile; never list it twice.
      const listed = new Set(this.repositoryDocuments.map(document => document.id));
      this.repositoryDocuments = [
        ...this.repositoryDocuments,
        ...response.items.filter(document => !listed.has(document.id)),
      ];
      this.repositoryPage = page;
      this.repositoryTotalPages = response.meta.totalPages ?? page;
    } catch {
      this.repositoryError = true;
    } finally {
      this.repositoryLoading = false;
    }
  }

  isRepositoryDocumentSelected(documentId: string) {
    return this.selectedRepositoryDocumentIds.includes(documentId);
  }

  toggleRepositoryDocument(documentId: string) {
    const selected = this.selectedRepositoryDocumentIds;
    const control = this.claimFormGroup.controls.repositoryDocumentIds;

    if (selected.includes(documentId)) control.setValue(selected.filter(id => id !== documentId));
    else if (!this.repositoryLimitReached) control.setValue([...selected, documentId]);
  }

  protected trackById(_index: number, document: WarrantyDocument.DocumentResponse) {
    return document.id;
  }

  protected async openClaim(warrantyId: string) {
    if (this.claimFormGroup.value.complaint) {
      const claim = await this.claimSvc
        .open({
          warrantyId: warrantyId,
          complaint: this.claimFormGroup.value.complaint,
          ...(this.claimFormGroup.value.lossDate && {
            lossDate: moment.utc(this.claimFormGroup.value.lossDate).toDate(),
          }),
          // Reported today in the customer's own timezone, saved like the loss date.
          dateReported: moment.utc(this.today).toDate(),
          items: this.claimFormGroup.value.coverages?.map(coverage =>
            coverage?.id
              ? { coverageId: coverage.id }
              : { coverageId: null, description: this.claimFormGroup.value.description }
          ),
        })
        .catch(err => {
          this.toastSvc.show({
            type: 'error',
            title: 'Failure',
            text: `The claim could not be created. Reason: ${err?.error?.message}! `,
          });
        });

      if (claim) {
        this.toastSvc.show({
          type: 'success',
          title: 'Success',
          text: 'A new claim has been created.',
        });

        // New files first, exactly as before; the claim stays created whatever happens to its evidence.
        const uploads = this.newFileCount ? await this.uploadFiles(claim.id) : null;
        const links = this.repositoryDocumentCount
          ? await this.linkRepositoryDocuments(claim.id, this.selectedRepositoryDocumentIds)
          : null;
        this.showEvidenceFeedback(uploads, links);

        await this.router.navigate(['../', claim?.id], { relativeTo: this.route });
      }
    }
  }

  async uploadFiles(_claimId: string): Promise<UploadOutcome> {
    const success = [];
    const error = [];

    if (this.claimFormGroup.controls.evidence.value) {
      for (let i = 0; i < this.claimFormGroup.controls.evidence.value.length; i++) {
        const file = this.claimFormGroup.controls.evidence.value[i];

        const claimEvidence = await this.claimSvc
          .createDocument(_claimId, {
            file,
            title: file.name ?? `file ${i}`,
            feed: ['staff', 'customer'],
          })
          .catch(err => {
            error.push(err);
            return null;
          });

        if (claimEvidence) {
          success.push(claimEvidence);
        }
      }
    }

    return { success: success.length, error: error.length };
  }

  async linkRepositoryDocuments(claimId: string, warrantyDocumentIds: string[]): Promise<LinkOutcome> {
    const response = await this.claimSvc.linkRepositoryDocuments(claimId, warrantyDocumentIds).catch(() => null);
    if (!response) return { success: 0, unavailable: 0, error: warrantyDocumentIds.length };

    const count = (...statuses: Claim.Documents.LinkRepositoryDocumentStatus[]) =>
      response.results.filter(result => statuses.includes(result.status)).length;
    return {
      success: count(LinkStatus.LINKED, LinkStatus.ALREADY_LINKED),
      unavailable: count(LinkStatus.UNAVAILABLE),
      error: count(LinkStatus.FAILED),
    };
  }

  /** One message for the claim's evidence; upload-only submissions keep their original messages. */
  private showEvidenceFeedback(uploads: UploadOutcome | null, links: LinkOutcome | null) {
    if (!links) {
      if (uploads) this.showUploadFeedback(uploads);
      return;
    }

    const success = (uploads?.success ?? 0) + links.success;
    const error = (uploads?.error ?? 0) + links.error;
    const unavailable = links.unavailable
      ? ` ${links.unavailable} selected ${links.unavailable === 1 ? 'document is' : 'documents are'} no longer available and ${links.unavailable === 1 ? 'was' : 'were'} not attached.`
      : '';

    if (!error && !links.unavailable) {
      this.toastSvc.show({
        type: 'success',
        title: 'Claim evidence',
        text: 'All of your evidence has been attached to your claim.',
      });
    } else if (!success && error) {
      this.toastSvc.show({
        type: 'error',
        title: 'Claim evidence',
        text: `Your evidence could not be attached, please review your claim attachments.${unavailable}`,
      });
    } else {
      this.toastSvc.show({
        type: 'warning',
        title: 'Claim evidence',
        text: `Some of your evidence could not be attached, please review your claim attachments.${unavailable}`,
      });
    }
  }

  private showUploadFeedback({ success, error }: UploadOutcome) {
    if (success && error) {
      this.toastSvc.show({
        type: 'warning',
        title: 'Files upload',
        text: 'Some of the files failed to upload, please review your claim attachments',
      });
    } else {
      if (success) {
        this.toastSvc.show({
          type: 'success',
          title: 'Files upload',
          text: 'All files have been successfully submitted for upload',
        });
      }

      if (error) {
        this.toastSvc.show({
          type: 'error',
          title: 'Files upload',
          text: 'Some of the files have failed to upload please review your claim attachments',
        });
      }
    }
  }
}
